import { type PropsWithChildren, type SelectHTMLAttributes, useId } from 'react'

import type { DefaultProps } from '#/global/component/Form/types'

import { Input as Helper } from '#/global/helper/DekForms/Input'

export type SelectList<V = Value> = (Option<V> | OptionGroup<V> | V)[]

interface Option<V = Value> { label: string, value: V }

interface OptionGroup<V = Value> { label: string, list: Option<V>[] }
interface Props extends DefaultProps, Omit<SelectProps, 'onChange' | 'size'> {
    list: SelectList
    onChange?: (v: string) => void
    value?: number | string
}
type SelectProps = SelectHTMLAttributes<HTMLSelectElement>

type Value = string | number

export const Select = ({
    children,
    className,
    list,
    onChange,
    size,
    state,
    ...props
}: PropsWithChildren<Props>) => {
    const id = useId()
    const formWidth = Helper.getFormSize(size)

    const { disabled, required } = props

    const handleChange: SelectProps['onChange'] = event => onChange?.(event.target.value)

    const classNameList = [formWidth]
    state && classNameList.push(state.level)
    className && classNameList.push(className)

    return (
        <>
            {children !== undefined && (
                <label htmlFor={id}>
                    {children}
                    {required && <span className="dek-form-required-indicator">*</span>}
                </label>
            )}
            <select
                className={classNameList.join(' ')}
                id={id}
                onChange={handleChange}
                role="combobox"
                {...props}>
                {list.map((item) => {
                    if (typeof item === 'number' || typeof item === 'string') {
                        return (
                            <option key={item} value={item}>
                                {item}
                            </option>
                        )
                    }
                    if ('list' in item) {
                        if (item.list.length === 0) {
                            return null
                        }
                        return (
                            <optgroup key={item.label} label={item.label}>
                                {item.list.map((option) => {
                                    return (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    )
                                })}
                            </optgroup>
                        )
                    } else {
                        if (item.value === null) {
                            return null
                        }
                        return (
                            <option key={item.value} value={item.value}>
                                {item.label}
                            </option>
                        )
                    }
                })}
            </select>
            {state && state.message && !disabled && <div className={`input-${state.level}`}>{state.message}</div>}
        </>
    )
}
